<div class="">
  <form [formGroup]="form" [formEditablePermission]="form" [class.loading-section]="isSaving$ | async">
    <div class="d-grid gap-3" style="grid-template-columns: auto auto">
      <!-- Check-in / Check-out Times -->
      <div class="card p-3">
        <h6 class="text-uppercase">
          <ng-container i18n>Check-in / Check-out Times</ng-container>&nbsp;
          <app-info-icon
            i18n-tooltipText
            tooltipText="The rental default check-in and check-out times are used for every new booking received from
                        a connected iCal channel. The check-in/out times of the booking will be set to these defaults automatically.
                        You can change the individual booking times on the booking detail page. These times are especially important
                        when creating time-based triggers. If you create triggers for before/after booking start or end then these
                        times will be used as reference."
            tooltipPosition="right"
          ></app-info-icon>
        </h6>
        <div class="row">
          <div class="col-sm-6">
            <mat-form-field2 class="mat-block">
              <input
                type="text"
                matInput
                atp-time-picker
                changeToMinutes="true"
                formControlName="checkin"
                placeholder="Check-in Time"
                i18n-placeholder
                required
              />
              <span matSuffix><i class="fal fa-clock"></i></span>
              <mat-error *ngIf="form.get('checkin').touched && form.get('checkin').hasError('required')">
                <ng-container i18n>Check-in time is required</ng-container>
              </mat-error>
            </mat-form-field2>
          </div>
          <div class="col-sm-6">
            <mat-form-field2 class="mat-block">
              <input
                type="text"
                matInput
                atp-time-picker
                changeToMinutes="true"
                formControlName="checkout"
                i18n-placeholder
                placeholder="Check-out Time"
                required
              />
              <span matSuffix><i class="fal fa-clock"></i></span>
              <mat-error *ngIf="form.get('checkout').touched && form.get('checkout').hasError('required')">
                <ng-container i18n>Check-out time is required</ng-container>
              </mat-error>
            </mat-form-field2>
          </div>
          <div class="col-sm-6">
            <app-timezone-select [ctrl]="form.get('timezone')"></app-timezone-select>
          </div>
        </div>
      </div>

      <!-- Codes & Passwords -->
      <div class="card p-3">
        <h6 class="text-uppercase" i18n>Codes & Passwords</h6>
        <div class="row" formGroupName="specifics">
          <div class="col-sm-12">
            <div class="row">
              <div class="col-sm-6">
                <mat-form-field2 class="mat-block">
                  <input type="text" matInput formControlName="wifi_name" i18n-placeholder placeholder="WiFi Name" />
                </mat-form-field2>
              </div>
              <div class="col-sm-6">
                <mat-form-field2 class="mat-block">
                  <input
                    type="text"
                    matInput
                    formControlName="wifi_pass"
                    i18n-placeholder
                    placeholder="WiFi Password"
                  />
                </mat-form-field2>
              </div>
              <div class="col-sm-6">
                <mat-form-field2 class="mat-block">
                  <input type="text" matInput formControlName="sec_code" i18n-placeholder placeholder="Security Code" />
                </mat-form-field2>
              </div>
              <div class="col-sm-6">
                <mat-form-field2 class="mat-block">
                  <input type="text" matInput formControlName="key_pickup" i18n-placeholder placeholder="Key Pickup" />
                </mat-form-field2>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ng-container formGroupName="instructions">
        <!-- Check-in Instructions -->
        <div class="card p-3">
          <h6 class="text-uppercase" i18n>Check-in Instructions</h6>
          <div class="row">
            <div class="col-sm-12 position-relative">
              <mat-form-field2 class="mat-block" cdkOverlayOrigin #aiCheckinSuggestionsTrigger="cdkOverlayOrigin">
                <app-mat-html-input
                  formControlName="checkin"
                  i18n-placeholder
                  placeholder="Enter Instructions here"
                ></app-mat-html-input>
              </mat-form-field2>
              <app-ai-suggestions-icon
                [entityId]="rental.id"
                type="rental_checkin"
                [attachTo]="aiCheckinSuggestionsTrigger"
                [width]="aiCheckinSuggestionsTrigger.elementRef?.nativeElement.clientWidth"
                (apply)="form.get('instructions.checkin').setValue($event)"
                tooltipText="Generate suggestions for the check-in instructions"
                i18n-tooltipText
                class="position-absolute"
                style="bottom: 2rem; right: 2rem"
              ></app-ai-suggestions-icon>
            </div>
          </div>
        </div>

        <!-- Check-out Instructions -->
        <div class="card p-3">
          <h6 class="text-uppercase" i18n>Check-out Instructions</h6>
          <div class="row">
            <div class="col-sm-12 position-relative">
              <mat-form-field2 class="mat-block" cdkOverlayOrigin #aiCheckoutSuggestionsTrigger="cdkOverlayOrigin">
                <app-mat-html-input
                  formControlName="checkout"
                  i18n-placeholder
                  placeholder="Enter Instructions here"
                ></app-mat-html-input>
              </mat-form-field2>
              <app-ai-suggestions-icon
                [entityId]="rental.id"
                type="rental_checkout"
                [attachTo]="aiCheckoutSuggestionsTrigger"
                [width]="aiCheckoutSuggestionsTrigger.elementRef?.nativeElement.clientWidth"
                (apply)="form.get('instructions.checkout').setValue($event)"
                tooltipText="Generate suggestions for the check-out instructions"
                i18n-tooltipText
                class="position-absolute"
                style="bottom: 2rem; right: 2rem"
              ></app-ai-suggestions-icon>
            </div>
          </div>
        </div>
        <!-- Directions -->
        <div class="card p-3">
          <h6 class="text-uppercase" i18n>Directions</h6>
          <div class="row">
            <div class="col-sm-12">
              <mat-form-field2 class="mat-block">
                <app-mat-html-input
                  formControlName="directions"
                  i18n-placeholder
                  placeholder="Enter Directions here"
                ></app-mat-html-input>
              </mat-form-field2>
            </div>
          </div>
        </div>

        <!-- House Rules -->
        <div class="card p-3">
          <h6 class="text-uppercase" i18n>House Rules</h6>
          <div class="row">
            <div class="col-sm-12 position-relative">
              <mat-form-field2 class="mat-block" cdkOverlayOrigin #aiRulesSuggestionsTrigger="cdkOverlayOrigin">
                <app-mat-html-input
                  formControlName="rules"
                  i18n-placeholder
                  placeholder="Enter House Rules here"
                ></app-mat-html-input>
              </mat-form-field2>
              <app-ai-suggestions-icon
                [entityId]="rental.id"
                type="rental_house_rules"
                [attachTo]="aiRulesSuggestionsTrigger"
                [width]="aiRulesSuggestionsTrigger.elementRef?.nativeElement.clientWidth"
                (apply)="form.get('instructions.rules').setValue($event)"
                tooltipText="Generate suggestions for the house rules"
                i18n-tooltipText
                class="position-absolute"
                style="bottom: 2rem; right: 2rem"
              ></app-ai-suggestions-icon>
            </div>
          </div>
        </div>
      </ng-container>
      <!-- Special Instructions -->
      <div class="card p-3" formGroupName="specifics">
        <h6 class="text-uppercase" i18n>Special Instructions</h6>
        <div class="row">
          <div class="col-sm-12">
            <mat-form-field2 class="mat-block">
              <textarea
                matInput
                formControlName="special_inst"
                placeholder="Enter Special Instructions here"
                rows="4"
                cdkTextareaAutosize
                i18n-placeholder
                cdkAutosizeMaxRows="8"
              ></textarea>
            </mat-form-field2>
          </div>
        </div>
      </div>
      <!-- Payment Terms -->
      <div class="card p-3">
        <h6 class="text-uppercase" i18n>Payment Terms</h6>
        <div class="row">
          <div class="col-sm-12">
            <mat-form-field2 class="mat-block">
              <textarea
                matInput
                formControlName="payment_terms"
                placeholder="Enter Payment Terms here"
                rows="4"
                cdkTextareaAutosize
                i18n-placeholder
                cdkAutosizeMaxRows="8"
              ></textarea>
            </mat-form-field2>
          </div>
        </div>
      </div>

      <!--Payment Instructions-->
      <div class="card p-3">
        <h6 class="text-uppercase" i18n>Payment Instructions</h6>
        <div class="row">
          <div class="col-sm-12">
            <mat-form-field2 class="mat-block">
              <textarea
                matInput
                formControlName="payment_instructions"
                placeholder="Enter Payment Instructions here"
                rows="4"
                i18n-placeholder
                cdkTextareaAutosize
                cdkAutosizeMaxRows="8"
              ></textarea>
            </mat-form-field2>
          </div>
        </div>
      </div>
    </div>
  </form>
</div>
